import { EventEmitter } from 'eventemitter3';
import { useNuxtApp } from '#app';
import { TimeUtils } from '@/utils/time';

let recentScrollEventTime: number = TimeUtils.dateNow();

interface SpatialNavigatorInterface {
    disable: () => void;
    enable: () => void;
    getCurrentFocused: () => HTMLElement | null;
    readonly isEnabled: boolean;
}

interface SpatialNavigatorEvents {
    focusin: (element: HTMLElement) => Promise<void> | void;
    keypress: (event: KeyboardEvent) => Promise<void> | void;
    "keypress:back": (event: KeyboardEvent) => Promise<void> | void;
    "keypress:down": (event: KeyboardEvent | HTMLElement | null) => Promise<void> | void;
    "keypress:left": (event: KeyboardEvent) => Promise<void> | void;
    "keypress:right": (event: KeyboardEvent) => Promise<void> | void;
    "keypress:up": (event: KeyboardEvent | HTMLElement | null) => Promise<void> | void;
    "keypress:enter": (event: KeyboardEvent) => Promise<void> | void;
    "keypress:number": (event: KeyboardEvent) => Promise<void> | void;
    "click": (event: HTMLElement | null) => Promise<void> | void;
}

export interface SpatialNavigatorOptions {
    readonly keyBack: Array<number | string>;
    readonly keyDown: Array<number | string>;
    readonly keyLeft: Array<number | string>;
    readonly keyRight: Array<number | string>;
    readonly keyUp: Array<number | string>;
    readonly keyEnter: Array<number | string>;
    readonly [key: string]: Array<number | string>;
}

export class SpatialNavigator
    extends EventEmitter<SpatialNavigatorEvents>
    implements SpatialNavigatorInterface {
    private readonly _options: SpatialNavigatorOptions;

    private _enabled: boolean = true;

    private _focused: HTMLElement | null = null;
    private downButtonEnabled = true;
    private leftButtonEnabled = true;
    private rightButtonEnabled = true;

    public constructor(options: SpatialNavigatorOptions) {
        super();

        this._options = options;

        document.addEventListener(
            "focusin",
            (event: FocusEvent): void => {
                this._focusChanged(event);
            },
            true
        );

        document.addEventListener(
            "keydown",
            (event: KeyboardEvent): void => {
                this._keyPress(event);
            },
            true
        );

        document.addEventListener(
            "wheel",
            (event: WheelEvent): void => {
                this._scroll(event);

            },
            { passive: false }
        );

        document.addEventListener(
            "click",
            (): void => {
                const { $router } = useNuxtApp();
                const isSearch = $router.currentRoute.value.name === 'search' ? true : false;

                if (document.activeElement?.id === 'page-content' && isSearch) {
                    document.getElementById("header-menu")?.focus();
                    return;
                } else if (document.activeElement?.tagName === "BODY") {
                    this._focused?.focus();
                    return;
                }
                this.emit("click", this._focused);
            }
        );


        if (document.activeElement instanceof HTMLElement) {
            this._focused = document.activeElement;
        }
    }

    public get isEnabled(): boolean {
        return this._enabled;
    }

    public static create(options: SpatialNavigatorOptions): SpatialNavigator {
        return new this(options);
    }

    public disable(): void {
        this._enabled = false;
    }

    public enable(): void {
        this._enabled = true;
    }

    public getCurrentFocused(): HTMLElement | null {
        return this._focused === null ? null : this._focused;
    }

    public throttle(cb: () => void, delay: number = 200): void {
        if (TimeUtils.dateNow() - recentScrollEventTime < delay) {
            return;
        }

        recentScrollEventTime = TimeUtils.dateNow();
        cb();
    }

    private _scroll(event: WheelEvent): void {
        event.preventDefault();
        if (this._focused !== null) {
            const target: Element | null = document.activeElement;
            if (event.deltaY > 0) {
                if (target !== undefined && target !== null) {
                    this._setFocused(target.id);
                }
                if (this.downButtonEnabled) {
                    this.throttle(() => {
                        this.emit("keypress:down", this._focused);
                    });
                }
            } else {
                if (target !== undefined && target !== null) {
                    this._setFocused(target.id);
                }
                this.throttle(() => {
                    this.emit("keypress:up", this._focused);
                });
            }
        }
    }

    private _focusChanged(event: FocusEvent): void {
        if (
            event.target === document.body &&
            this._focused instanceof HTMLElement
        ) {
            this._focused.focus();
            return;
        }
        if (event.target instanceof HTMLElement) {
            this._focused = event.target;
            this.emit("focusin", this._focused);
        }
    }

    private _keyPress(event: KeyboardEvent): void {
        if (!this.isEnabled) {
            event.preventDefault();
            return;
        }

        const isKeyNumber: boolean = this._options.keyNumber.some(
            (key: number | string): boolean => 
                key === event.key || key === event.keyCode
        );

        if (isKeyNumber) {
            this._keyPressNumber(event);
        }

        const isKeyEnter: boolean = this._options.keyEnter.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyEnter) {
            this._keyPressEnter(event);
        }

        const isKeyBack: boolean = this._options.keyBack.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyBack) {
            this._keyPressBack(event);
            return;
        }

        const isKeyLeft: boolean = this._options.keyLeft.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyLeft) {
            this._keyPressLeft(event);
            return;
        }

        const isKeyUp: boolean = this._options.keyUp.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyUp) {
            this._keyPressUp(event);
            return;
        }

        const isKeyRight: boolean = this._options.keyRight.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyRight) {
            this._keyPressRight(event);
            return;
        }

        const isKeyDown: boolean = this._options.keyDown.some(
            (key: number | string): boolean =>
                key === event.key || key === event.keyCode
        );

        if (isKeyDown) {
            this._keyPressDown(event);
        }

        this.emit("keypress", event);
    }

    private _keyPressBack(event: KeyboardEvent): void {
        event.preventDefault();
        this.throttle(() => {
            this.emit("keypress:back", event);
        }, 1000);
    }

    private _keyPressDown(event: KeyboardEvent): void {
        event.preventDefault();
        if (this.downButtonEnabled && this._focused !== null) {
            const target: string | undefined = this._focused.dataset.keydown;
            if (target !== undefined) {
                this._setFocused(target);
                return;
            }
        }

        if (this.downButtonEnabled) {
            this.emit("keypress:down", event);
        }
    }

    private _keyPressLeft(event: KeyboardEvent): void {
        if (this.leftButtonEnabled) {
            event.preventDefault();

            if (this._focused !== null) {
                const target: string | undefined = this._focused.dataset.keyleft;

                if (target !== undefined) {
                    this._setFocused(target);
                    return;
                }
            }

            this.emit("keypress:left", event);
        }
    }

    private _keyPressRight(event: KeyboardEvent): void {
        if (this.rightButtonEnabled) {
            event.preventDefault();

            if (this._focused !== null) {
                const target: string | undefined = this._focused.dataset.keyright;

                if (target !== undefined) {
                    this._setFocused(target);
                    return;
                }
            }

            this.emit("keypress:right", event);
        }
    }

    private _keyPressUp(event: KeyboardEvent): void {
        event.preventDefault();

        if (this._focused !== null) {
            const target: string | undefined = this._focused.dataset.keyup;

            if (target !== undefined) {
                this._setFocused(target);
                return;
            }
        }

        this.emit("keypress:up", event);
    }

    private _keyPressNumber(event: KeyboardEvent): void {
        this.emit("keypress:number", event);
    }

    private _keyPressEnter(event: KeyboardEvent): void {
        this.emit("keypress:enter", event);
    }

    private _setFocused(id: string): void {
        const element: HTMLElement | null = document.getElementById(id) as HTMLElement;
        if (element instanceof HTMLElement) {
            const preventScroll = element.dataset.noFocusScroll !== undefined;
            element.focus({ preventScroll });
        }
    }

    enableDownButton() {
        this.downButtonEnabled = true;
    }

    disableDownButton() {
        this.downButtonEnabled = false;
    }

    disableLeftButton() {
        this.leftButtonEnabled = false;
    }
    enableLeftButton() {
        this.leftButtonEnabled = true;
    }

    disableRightButton() {
        this.rightButtonEnabled = false;
    }
    enableRightButton() {
        this.rightButtonEnabled = true;
    }

    disableLeftAndRightButtons() {
        this.disableRightButton();
        this.disableLeftButton();
    }

    enableLeftAndRightButtons() {
        this.enableRightButton();
        this.enableLeftButton();
    }

    clearEvents() {
        this.removeAllListeners();
    }
}
